// src/pages/CodingTerminal.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { sendTerminalStreaming } from '../services/terminalChat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Fuse from 'fuse.js';
import { useNavigate } from 'react-router-dom';
import {
  loadSessions as loadServerSessions,
  saveSession as saveServerSession,
  deleteSession as deleteServerSession,
} from '../services/sessionSync';

const STORAGE_KEY = 'forgeSessions';
const CURRENT_KEY = 'forgeCurrentSessionId';
const MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;
const MAX_TEXT_PREVIEW_CHARS = 120_000;

const createNewSession = (idSuffix = '') => ({
  id: `forge-${Date.now()}${idSuffix}`,
  title: 'New session',
  messages: [],
  createdAt: Date.now(),
});

const EXT_MAP = {
  python: 'py',
  javascript: 'js',
  typescript: 'ts',
  jsx: 'jsx',
  tsx: 'tsx',
  html: 'html',
  css: 'css',
  sql: 'sql',
  bash: 'sh',
  json: 'json',
  yaml: 'yml',
  markdown: 'md',
  plaintext: 'txt',
};

const IMAGE_MIME_RE = /^image\/(png|jpe?g|webp|gif|bmp|svg\+xml)$/i;
const TEXT_MIME_RE =
  /^text\/|application\/(json|xml|javascript|x-javascript|x-typescript|typescript|x-sh|x-shellscript|yaml|yml|toml)$/i;

function isImageFile(file) {
  return IMAGE_MIME_RE.test(file.type) || /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(file.name);
}

function isTextFile(file) {
  return (
    TEXT_MIME_RE.test(file.type) ||
    /\.(txt|md|markdown|json|ya?ml|xml|csv|ts|tsx|js|jsx|mjs|cjs|py|sh|bash|zsh|sql|css|scss|html|htm|env|toml|ini|cfg|log|graphql|gql)$/i.test(
      file.name
    )
  );
}

function fileKind(file) {
  if (isImageFile(file)) return 'image';
  if (isTextFile(file)) return 'text';
  return 'binary';
}

function safeDownloadName(name) {
  return (name || 'forge_attachment').replace(/[^a-z0-9-_.]/gi, '_');
}

async function readFileAsText(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => resolve(String(evt.target?.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file as text'));
    reader.readAsText(file);
  });
}

async function readFileAsDataURL(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => resolve(String(evt.target?.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file as image'));
    reader.readAsDataURL(file);
  });
}

const MD_COMPONENTS = {
  h1: ({ node, ...p }) => <h1 className="mb-1.5 mt-2 text-[1.02rem] font-bold leading-tight text-[#ccffdd]" {...p} />,
  h2: ({ node, ...p }) => <h2 className="mb-1 mt-2 text-[0.98rem] font-semibold leading-tight text-[#ccffdd]" {...p} />,
  h3: ({ node, ...p }) => <h3 className="mb-1 mt-1.5 text-[0.95rem] font-medium leading-tight text-[#ccffdd]/95" {...p} />,
  ul: ({ node, ...p }) => <ul className="mb-2 list-disc space-y-0.5 pl-5" {...p} />,
  ol: ({ node, ...p }) => <ol className="mb-2 list-decimal space-y-0.5 pl-5" {...p} />,
  li: ({ node, ...p }) => <li className="leading-6" {...p} />,
  p: ({ node, ...p }) => <p className="mb-2 last:mb-0 leading-6 text-[14px] text-[#e0ffe8]" {...p} />,
  a: ({ node, ...p }) => <a className="text-[#00ff9d] underline transition-colors hover:text-[#7cffc7]" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-white" {...p} />,
  blockquote: ({ node, ...p }) => (
    <blockquote className="my-2 border-l-2 border-[#00ff9d]/25 pl-3 text-[#d7ffe8]/90" {...p} />
  ),
  table: ({ node, ...p }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full overflow-hidden rounded-lg border border-[#00ff9d]/15" {...p} />
    </div>
  ),
  th: ({ node, ...p }) => (
    <th className="border-b border-[#00ff9d]/15 bg-white/5 px-2.5 py-1.5 text-left text-[13px]" {...p} />
  ),
  td: ({ node, ...p }) => (
    <td className="border-b border-white/5 px-2.5 py-1.5 align-top text-[13px] leading-6" {...p} />
  ),
  code: ({ node, inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const codeString = String(children).replace(/\n$/, '');
    const lang = match?.[1] || 'plaintext';

    if (!inline) {
      const copyCode = async () => {
        try {
          await navigator.clipboard.writeText(codeString);
        } catch {}
      };

      const downloadCode = () => {
        const ext = EXT_MAP[lang] || 'txt';
        const filenameFromProps =
          typeof props['data-filename'] === 'string' && props['data-filename'].trim()
            ? props['data-filename'].trim()
            : null;

        const safeName = filenameFromProps ? safeDownloadName(filenameFromProps) : `forge_code.${ext}`;
        const finalName = safeName.toLowerCase().endsWith(`.${ext}`) ? safeName : `${safeName}.${ext}`;

        const blob = new Blob([codeString], { type: 'text/plain;charset=utf-8' });
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = finalName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      };

      return (
        <div className="group relative my-3">
          <div className="absolute right-2 top-2 z-10 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              onClick={copyCode}
              className="rounded-md border border-[#00ff9d]/25 bg-black/80 px-2 py-1 text-[11px] text-[#e0ffe8] shadow-lg backdrop-blur hover:border-[#00ff9d]/45 hover:text-[#00ff9d]"
            >
              Copy
            </button>
            <button
              onClick={downloadCode}
              className="rounded-md border border-[#00ff9d]/25 bg-black/80 px-2 py-1 text-[11px] text-[#e0ffe8] shadow-lg backdrop-blur hover:border-[#00ff9d]/45 hover:text-[#00ff9d]"
            >
              Download
            </button>
          </div>
          <SyntaxHighlighter
            style={vscDarkPlus}
            language={lang}
            PreTag="div"
            customStyle={{
              borderRadius: '12px',
              padding: '0.85rem',
              fontSize: '13px',
              lineHeight: '1.45',
              background: '#0b0b0b',
              border: '1px solid rgba(0,255,157,0.18)',
              boxShadow: '0 0 0 1px rgba(0,255,157,0.04), 0 0 24px rgba(0,255,157,0.05)',
              margin: 0,
            }}
          >
            {codeString}
          </SyntaxHighlighter>
        </div>
      );
    }

    return (
      <code className="rounded-md bg-white/5 px-1.5 py-0.5 font-mono text-[0.9em] text-[#a0ffcc]" {...props}>
        {children}
      </code>
    );
  },
};

const AttachmentPreview = memo(function AttachmentPreview({ attachment, onRemove }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-[#00ff9d]/15 bg-white/5 px-3 py-2">
      {attachment.kind === 'image' ? (
        <img
          src={attachment.preview}
          alt={attachment.name}
          className="h-10 w-10 rounded-lg border border-[#00ff9d]/15 object-cover"
        />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#00ff9d]/15 bg-black/30 text-[10px] text-[#00ff9d]/80">
          {attachment.kind === 'text' ? 'TXT' : 'BIN'}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] text-[#e0ffe8]">{attachment.name}</div>
        <div className="text-[10px] text-[#a0ffcc]/55">
          {attachment.kind} • {(attachment.size / 1024).toFixed(1)} KB
        </div>
      </div>

      <button
        onClick={() => onRemove(attachment.id)}
        className="rounded-md border border-[#00ff9d]/15 bg-black/30 px-2 py-1 text-[11px] text-[#a0ffcc]/75 hover:border-[#00ff9d]/35 hover:text-[#00ff9d]"
      >
        Remove
      </button>
    </div>
  );
});

const ForgeMessages = memo(
  ({ messages, streamingContent, isLoading, statusMessage, currentSessionId, messagesEndRef }) => (
    <div className="space-y-2.5 pb-4 pt-3">
      {messages.length === 0 && !streamingContent && !isLoading ? (
        <div className="flex min-h-[52vh] select-none flex-col items-center justify-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#00ff9d]/25 bg-[#00ff9d]/10 text-3xl font-black text-[#00ff9d] shadow-[0_0_30px_rgba(0,255,157,0.12)]">
            F
          </div>
          <h1 className="mb-1.5 text-2xl font-bold tracking-[0.2em] text-[#ccffdd]">FORGE</h1>
          <p className="text-[11px] text-[#a0ffcc]/45">Private Coding Terminal • Azure GPT-5</p>
          <p className="mt-5 max-w-md text-[13px] leading-6 text-[#e0ffe8]/70">
            Write code, debug, refactor,
            <br />
            or ask for architecture guidance.
          </p>
        </div>
      ) : (
        messages.map((msg, idx) => (
          <div key={`${currentSessionId}-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'user' ? (
              <div className="flex max-w-[78%] items-end gap-2">
                <div className="whitespace-pre-wrap rounded-2xl rounded-br-md border border-[#00ff9d]/18 bg-[#00ff9d]/10 px-3.5 py-2.5 text-[14px] leading-6 text-[#eafff4]">
                  {msg.content}
                </div>
                <div className="mb-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#00ff9d]/18 text-[10px] font-bold text-[#00ff9d]">
                  $
                </div>
              </div>
            ) : (
              <div className="flex max-w-[88%] items-start gap-2.5">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-[#00ff9d] text-[11px] font-black text-black shadow-[0_0_18px_rgba(0,255,157,0.2)]">
                  F
                </div>
                <div className="prose prose-invert max-w-none rounded-2xl rounded-bl-md border border-[#00ff9d]/14 bg-[#111] px-4 py-3 text-[14px] leading-6 text-[#e0ffe8] shadow-[0_0_0_1px_rgba(255,255,255,0.02)] prose-headings:my-0 prose-ol:my-0 prose-p:my-0 prose-ul:my-0">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                    {msg.content}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ))
      )}

      {(isLoading || streamingContent) && (
        <div className="flex justify-start">
          <div className="flex max-w-[88%] items-start gap-2.5">
            <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-[#00ff9d] text-[11px] font-black text-black shadow-[0_0_18px_rgba(0,255,157,0.2)]">
              F
            </div>
            <div className="prose prose-invert max-w-none rounded-2xl rounded-bl-md border border-[#00ff9d]/14 bg-[#111] px-4 py-3 text-[14px] leading-6 text-[#e0ffe8] prose-headings:my-0 prose-ol:my-0 prose-p:my-0 prose-ul:my-0">
              {streamingContent ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                  {streamingContent}
                </ReactMarkdown>
              ) : (
                <div className="flex items-center gap-2.5 text-[13px] text-[#a0ffcc]/55">
                  <div className="flex gap-1">
                    {[0, 120, 240].map((d) => (
                      <div
                        key={d}
                        className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#00ff9d]"
                        style={{ animationDelay: `${d}ms` }}
                      />
                    ))}
                  </div>
                  <span>{statusMessage || 'Forge is thinking...'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} className="h-2" />
    </div>
  )
);

function CodingTerminal() {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [streamingContent, setStreamingContent] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionSearch, setSessionSearch] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [renamingSessionId, setRenamingSessionId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [inputHistory, setInputHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [mode, setMode] = useState('coding');
  const [followLatest, setFollowLatest] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [showJumpTop, setShowJumpTop] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const draftRef = useRef('');
  const navigate = useNavigate();

  const MIN_INTERVAL = 3000;

  useEffect(() => {
    (async () => {
      let init = [];
      let initId = null;

      const serverSessions = await loadServerSessions('forge');
      if (serverSessions?.length > 0) {
        init = serverSessions;
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(init));
        } catch {}
      } else {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) init = JSON.parse(stored);
        } catch {}
      }

      if (init.length > 0) {
        const storedId = localStorage.getItem(CURRENT_KEY);
        initId = storedId && init.find((s) => s.id === storedId) ? storedId : init.sort((a, b) => b.createdAt - a.createdAt)[0].id;
      } else {
        const s = createNewSession('-init');
        init = [s];
        initId = s.id;
      }

      setSessions(init);
      setCurrentSessionId(initId);
    })();
  }, []);

  useEffect(() => {
    if (!sessions.length || !currentSessionId) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
        localStorage.setItem(CURRENT_KEY, currentSessionId);
      } catch {}
      const current = sessions.find((s) => s.id === currentSessionId);
      if (current?.messages.length > 0) saveServerSession('forge', current);
    }, 1000);
    return () => clearTimeout(timer);
  }, [sessions, currentSessionId]);

  const currentSession = useMemo(() => sessions.find((s) => s.id === currentSessionId), [sessions, currentSessionId]);
  const currentMessages = useMemo(() => currentSession?.messages || [], [currentSession]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  const scrollToTop = useCallback(() => {
    messagesScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const onMessagesScroll = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceToBottom < 140;
    setIsNearBottom(nearBottom);
    setShowJumpTop(el.scrollTop > 250);
    if (nearBottom) setNewMessageCount(0);
  }, []);

  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;

    const onScroll = () => onMessagesScroll();
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [onMessagesScroll]);

  useEffect(() => {
    if (followLatest && isNearBottom) scrollToBottom('auto');
  }, [currentMessages, streamingContent, followLatest, isNearBottom, scrollToBottom]);

  useEffect(() => {
    if (followLatest) scrollToBottom('smooth');
  }, [currentMessages, streamingContent, isLoading, followLatest, scrollToBottom]);

  const fuse = useMemo(() => new Fuse(sessions, { keys: ['title'], threshold: 0.35 }), [sessions]);
  const filteredSessions = sessionSearch ? fuse.search(sessionSearch).map((r) => r.item) : [...sessions].sort((a, b) => b.createdAt - a.createdAt);

  const resetComposer = useCallback(() => {
    setInput('');
    setAttachments([]);
    setInputHistory([]);
    setHistoryIndex(-1);
    setShowHistory(false);
    setRenamingSessionId(null);
    setRenameValue('');
    setFollowLatest(true);
    setNewMessageCount(0);
    draftRef.current = '';
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, []);

  const handleNewChat = useCallback(() => {
    const s = createNewSession();
    setSessions((prev) => [s, ...prev]);
    setCurrentSessionId(s.id);
    resetComposer();
  }, [resetComposer]);

  const handleSelectSession = useCallback(
    (id) => {
      setCurrentSessionId(id);
      resetComposer();
    },
    [resetComposer]
  );

  const handleDeleteSession = useCallback(
    (id, e) => {
      e.stopPropagation();
      deleteServerSession('forge', id);
      setSessions((prev) => {
        const remaining = prev.filter((s) => s.id !== id);
        if (remaining.length === 0) {
          const s = createNewSession('-empty');
          setCurrentSessionId(s.id);
          return [s];
        }
        if (currentSessionId === id) {
          setCurrentSessionId([...remaining].sort((a, b) => b.createdAt - a.createdAt)[0].id);
        }
        return remaining;
      });
    },
    [currentSessionId]
  );

  const handleRenameStart = useCallback((session) => {
    setRenamingSessionId(session.id);
    setRenameValue(session.title);
  }, []);

  const handleRenameSave = useCallback(() => {
    if (!renamingSessionId) return;
    const nextTitle = renameValue.trim() || 'New session';
    setSessions((prev) => prev.map((s) => (s.id === renamingSessionId ? { ...s, title: nextTitle } : s)));
    setRenamingSessionId(null);
    setRenameValue('');
  }, [renamingSessionId, renameValue]);

  const handleCopySession = useCallback(
    async (id) => {
      const session = sessions.find((s) => s.id === id);
      if (!session) return;
      const content = session.messages.map((m) => `${m.role === 'user' ? 'User' : 'Forge'}:\n${m.content}`).join('\n\n---\n\n');
      try {
        await navigator.clipboard.writeText(content);
      } catch {}
    },
    [sessions]
  );

  const handleExportSession = useCallback(
    (id) => {
      const session = sessions.find((s) => s.id === id);
      if (!session) return;
      const content = session.messages
        .map((m) => (m.role === 'user' ? `**User:**\n${m.content}` : `**Forge:**\n${m.content}`))
        .join('\n\n---\n\n');
      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = `${safeDownloadName(session.title)}.md`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    },
    [sessions]
  );

  const addAttachments = useCallback(async (files) => {
    const fileList = Array.from(files || []);
    if (!fileList.length) return;

    const next = [];
    for (const file of fileList) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        alert(`"${file.name}" is too large. Max size is 12MB.`);
        continue;
      }

      const kind = fileKind(file);

      if (kind === 'image') {
        const preview = await readFileAsDataURL(file);
        next.push({
          id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          name: file.name,
          size: file.size,
          type: file.type,
          kind,
          preview,
          text: null,
        });
      } else if (kind === 'text') {
        let text = await readFileAsText(file);
        if (text.length > MAX_TEXT_PREVIEW_CHARS) {
          text = text.slice(0, MAX_TEXT_PREVIEW_CHARS) + '\n\n[TRUNCATED FOR SAFETY]';
        }
        next.push({
          id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          name: file.name,
          size: file.size,
          type: file.type,
          kind,
          preview: null,
          text,
        });
      } else {
        next.push({
          id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          name: file.name,
          size: file.size,
          type: file.type,
          kind: 'binary',
          preview: null,
          text: null,
        });
      }
    }

    setAttachments((prev) => [...prev, ...next]);
  }, []);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const files = e.dataTransfer.files;
      if (files?.length) await addAttachments(files);
    },
    [addAttachments]
  );

  const handleSend = useCallback(async () => {
    if ((!input.trim() && attachments.length === 0) || !currentSessionId || isLoading) return;

    const now = Date.now();
    if (now - lastRequestTime < MIN_INTERVAL) {
      alert(`Please wait ${Math.ceil((MIN_INTERVAL - (now - lastRequestTime)) / 1000)}s`);
      return;
    }
    setLastRequestTime(now);

    setInputHistory((prev) => [input, ...prev].slice(0, 30));
    setHistoryIndex(-1);

    const attachmentSummary = attachments
      .map((a) => {
        if (a.kind === 'image') return `Attached image: ${a.name}`;
        if (a.kind === 'text') return `Attached text file: ${a.name}\n\n${a.text}`;
        return `Attached file: ${a.name} (${a.type || 'unknown type'})`;
      })
      .join('\n\n');

    const userContent = [input.trim(), attachmentSummary].filter(Boolean).join('\n\n');
    const userMsg = { role: 'user', content: userContent };

    const session = sessions.find((s) => s.id === currentSessionId);
    if (!session) return;

    const msgs = [...session.messages, userMsg];
    let title = session.title;
    if (session.messages.length === 0 && session.title === 'New session') {
      title = `Forge: ${input.substring(0, 25)}${input.length > 25 ? '...' : ''}` || 'Forge: Attachment';
    }

    setSessions((prev) => prev.map((s) => (s.id === currentSessionId ? { ...s, messages: msgs, title } : s)));
    const currentInput = input;
    const currentAttachments = attachments;
    draftRef.current = '';
    setInput('');
    setAttachments([]);
    setIsLoading(true);
    setFollowLatest(true);
    setNewMessageCount(0);

    try {
      const recentHistory = msgs.slice(-6);
      let acc = '';
      await sendTerminalStreaming(
        '/coding/stream',
        currentInput,
        recentHistory,
        {
          attachments: currentAttachments,
          onStatus: (s) => setStatusMessage(s),
          onChunk: (text) => {
            acc += text;
            setStreamingContent(acc);
            setStatusMessage('');
          },
          onDone: (result) => {
            setSessions((prev) =>
              prev.map((s) =>
                s.id === currentSessionId
                  ? { ...s, messages: [...msgs, { role: 'assistant', content: result.content }] }
                  : s
              )
            );
            setStreamingContent('');
            setStatusMessage('');
          },
          onError: (err) => {
            console.error('Forge stream error:', err);
            setSessions((prev) =>
              prev.map((s) =>
                s.id === currentSessionId
                  ? { ...s, messages: [...msgs, { role: 'assistant', content: `Error: ${err?.error || 'Unknown error'}` }] }
                  : s
              )
            );
            setStreamingContent('');
            setStatusMessage('');
          },
        },
        { mode }
      );
    } catch (error) {
      console.error('Forge streaming failed:', error);
      setSessions((prev) =>
        prev.map((s) =>
          s.id === currentSessionId
            ? { ...s, messages: [...msgs, { role: 'assistant', content: `Connection error: ${error.message}` }] }
            : s
        )
      );
      setStreamingContent('');
      setStatusMessage('');
    } finally {
      setIsLoading(false);
    }
  }, [input, attachments, currentSessionId, lastRequestTime, sessions, mode, isLoading]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
        return;
      }

      const ta = textareaRef.current;
      if (!ta) return;

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const atStart = ta.selectionStart === 0 && ta.selectionEnd === 0;
        const atEnd = ta.selectionStart === input.length && ta.selectionEnd === input.length;

        if ((e.key === 'ArrowUp' && !atStart) || (e.key === 'ArrowDown' && !atEnd)) return;
        if (inputHistory.length === 0) return;

        e.preventDefault();

        setHistoryIndex((idx) => {
          if (idx === -1) draftRef.current = input;

          const nextIdx =
            e.key === 'ArrowUp' ? Math.min(inputHistory.length - 1, idx + 1) : Math.max(-1, idx - 1);

          if (nextIdx === -1) {
            setInput(draftRef.current);
          } else {
            setInput(inputHistory[nextIdx] || '');
          }

          return nextIdx;
        });
      }
    },
    [handleSend, inputHistory, input]
  );

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
  }, [input]);

  return (
    <div
      className="fixed inset-0 flex overflow-hidden bg-[#0a0a0a] font-mono text-[#e0ffe8]"
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)',
        backgroundSize: '100% 42px, 42px 100%',
      }}
    >
      <div style={{ width: 58, flexShrink: 0, background: '#141414', borderRight: '2px solid rgba(0,255,157,0.35)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '16px 0', zIndex: 60 }}>
        <button
          onClick={() => setShowHistory(true)}
          className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[#00ff9d]/20 bg-[#00ff9d] text-base font-black text-black shadow-[0_0_24px_rgba(0,255,157,0.18)] transition-all hover:scale-105 active:scale-95"
          title="Open chat history"
        >
          F
        </button>

        <button
          onClick={() => navigate('/health')}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#00ff9d]/12 bg-[#00ff9d]/8 transition-all hover:border-[#00ff9d]/25 hover:bg-[#00ff9d]/14"
          title="Back to AI Sentry"
        >
          <svg className="h-4 w-4 text-[#a0ffcc]/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" />
          </svg>
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#00ff9d]/12 bg-[#00ff9d]/8 transition-all hover:border-[#00ff9d]/25 hover:bg-[#00ff9d]/14"
          title="Upload file"
        >
          <svg className="h-4 w-4 text-[#a0ffcc]/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={async (e) => {
            if (e.target.files?.length) await addAttachments(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {showHistory && <div className="fixed inset-0 z-40 bg-black/45 backdrop-blur-[1px]" onClick={() => setShowHistory(false)} />}

      <div
        className={`fixed inset-y-0 left-[58px] z-50 flex w-[320px] flex-col border-r border-[#00ff9d]/12 bg-[#0d0d0d] shadow-2xl transition-transform duration-300 ${
          showHistory ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-[#00ff9d]/10 px-4 py-3">
          <span className="text-sm font-semibold tracking-[0.16em] text-[#e0ffe8]">History</span>
          <button onClick={() => setShowHistory(false)} className="text-2xl leading-none text-[#a0ffcc]/55 hover:text-[#00ff9d]">
            ×
          </button>
        </div>

        <div className="px-3 py-3">
          <button
            onClick={handleNewChat}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-[#00ff9d]/20 bg-[#00ff9d]/10 py-2.5 text-sm font-semibold text-[#eafff4] transition-all hover:border-[#00ff9d]/35 hover:bg-[#00ff9d]/14"
          >
            + New chat
          </button>
        </div>

        <div className="px-3 pb-2">
          <input
            value={sessionSearch}
            onChange={(e) => setSessionSearch(e.target.value)}
            placeholder="Search sessions..."
            className="w-full rounded-2xl border border-[#00ff9d]/12 bg-[#111] px-3 py-2 text-sm text-[#e0ffe8] outline-none placeholder:text-[#a0ffcc]/30 focus:border-[#00ff9d]/35"
          />
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-4 text-sm">
          {filteredSessions.map((s) => {
            const isActive = s.id === currentSessionId;
            const isRenaming = renamingSessionId === s.id;

            return (
              <div
                key={s.id}
                onClick={() => !isRenaming && handleSelectSession(s.id)}
                className={`cursor-pointer rounded-2xl border px-3 py-2.5 transition-all ${
                  isActive
                    ? 'border-[#00ff9d]/25 bg-[#00ff9d]/10'
                    : 'border-transparent bg-transparent hover:border-[#00ff9d]/10 hover:bg-[#00ff9d]/5'
                }`}
              >
                {isRenaming ? (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSave();
                        if (e.key === 'Escape') {
                          setRenamingSessionId(null);
                          setRenameValue('');
                        }
                      }}
                      className="w-full rounded-xl border border-[#00ff9d]/12 bg-[#111] px-3 py-2 text-sm text-[#e0ffe8] outline-none focus:border-[#00ff9d]/35"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleRenameSave}
                        className="rounded-lg border border-[#00ff9d]/20 bg-[#00ff9d]/12 px-3 py-1.5 text-xs font-semibold text-[#eafff4]"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setRenamingSessionId(null);
                          setRenameValue('');
                        }}
                        className="rounded-lg border border-[#00ff9d]/10 bg-black/30 px-3 py-1.5 text-xs text-[#a0ffcc]/70"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`truncate font-medium leading-5 ${isActive ? 'text-[#eafff4]' : 'text-[#d7ffe8]/82'}`}>{s.title}</div>
                    <div className="mt-0.5 text-[10px] text-[#a0ffcc]/35">
                      {s.messages.length} messages • {new Date(s.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                    </div>

                    <div className="mt-2 flex gap-1.5 opacity-95" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleRenameStart(s)}
                        className="rounded-md border border-[#00ff9d]/10 bg-black/30 px-2 py-1 text-[10px] text-[#a0ffcc]/75 hover:border-[#00ff9d]/25 hover:text-[#00ff9d]"
                      >
                        Rename
                      </button>
                      <button
                        onClick={() => handleCopySession(s.id)}
                        className="rounded-md border border-[#00ff9d]/10 bg-black/30 px-2 py-1 text-[10px] text-[#a0ffcc]/75 hover:border-[#00ff9d]/25 hover:text-[#00ff9d]"
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => handleExportSession(s.id)}
                        className="rounded-md border border-[#00ff9d]/10 bg-black/30 px-2 py-1 text-[10px] text-[#a0ffcc]/75 hover:border-[#00ff9d]/25 hover:text-[#00ff9d]"
                      >
                        Export
                      </button>
                      <button
                        onClick={(e) => handleDeleteSession(s.id, e)}
                        className="rounded-md border border-red-500/20 bg-black/30 px-2 py-1 text-[10px] text-red-200 hover:bg-red-500/10"
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden pl-px">
        <div className="flex h-11 flex-shrink-0 items-center gap-3 border-b border-[#00ff9d]/10 bg-[#0a0a0a]/90 px-4 backdrop-blur-sm">
          <div className="min-w-0 truncate text-sm font-medium text-[#e0ffe8]/75">
            {currentSession?.title || 'Forge Terminal'}
          </div>

          <div className="ml-auto flex items-center gap-2 text-xs">
            <button
              onClick={() => setFollowLatest((v) => !v)}
              className={`rounded-full border px-3 py-1 text-[10px] font-semibold transition-all ${
                followLatest
                  ? 'border-[#00ff9d]/30 bg-[#00ff9d]/12 text-[#eafff4]'
                  : 'border-[#00ff9d]/12 bg-black/30 text-[#a0ffcc]/60'
              }`}
              title="Toggle auto-follow latest messages"
            >
              FOLLOW: {followLatest ? 'ON' : 'OFF'}
            </button>

            {isLoading && (
              <button
                onClick={() => setIsLoading(false)}
                className="rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-[10px] font-semibold text-red-100"
                title="Stop generating"
              >
                STOP
              </button>
            )}

            <div className="flex rounded-full border border-[#00ff9d]/10 bg-black/60 p-0.5">
              {['coding', 'general'].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`rounded-full px-3.5 py-1 text-[10px] font-semibold transition-all ${
                    mode === m ? 'bg-[#00ff9d] text-black' : 'text-[#a0ffcc]/60 hover:text-[#00ff9d]'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div ref={messagesScrollRef} className="flex-1 overflow-y-auto px-4 md:px-5 lg:px-6">
          <ForgeMessages
            messages={currentMessages}
            streamingContent={streamingContent}
            isLoading={isLoading}
            statusMessage={statusMessage}
            currentSessionId={currentSessionId}
            messagesEndRef={messagesEndRef}
          />
        </div>

        <div className="flex-shrink-0 border-t border-[#00ff9d]/10 bg-[#0a0a0a]/95 p-2.5 backdrop-blur-sm md:p-3">
          <div className="mx-auto max-w-5xl">
            {attachments.length > 0 && (
              <div className="mb-2.5 space-y-2">
                {attachments.map((att) => (
                  <AttachmentPreview
                    key={att.id}
                    attachment={att}
                    onRemove={(id) => setAttachments((prev) => prev.filter((a) => a.id !== id))}
                  />
                ))}
              </div>
            )}

            <div className="rounded-2xl border border-[#00ff9d]/14 bg-[#111]/90 p-2.5 shadow-[0_0_0_1px_rgba(255,255,255,0.02)]">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (historyIndex !== -1) {
                    setHistoryIndex(-1);
                    draftRef.current = e.target.value;
                  }
                }}
                onKeyDown={handleKeyDown}
                placeholder="Ask Forge to code, debug, refactor, or explain..."
                className="min-h-[48px] max-h-[200px] w-full resize-none rounded-xl border border-transparent bg-transparent px-1 py-0.5 text-[14px] leading-6 text-[#e0ffe8] outline-none placeholder:text-[#a0ffcc]/25"
                rows={1}
              />

              <div className="mt-1.5 flex items-center justify-between gap-3">
                <div className="text-[10px] text-[#a0ffcc]/40">
                  Enter to send • Shift+Enter for newline • {followLatest ? 'Auto-follow enabled' : 'Auto-follow disabled'}
                  {newMessageCount > 0 ? ` • ${newMessageCount} new` : ''}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-lg border border-[#00ff9d]/15 bg-black/30 px-3 py-1.5 text-[11px] text-[#a0ffcc]/70 transition-all hover:border-[#00ff9d]/30 hover:text-[#00ff9d]"
                    title="Attach files"
                  >
                    Attach
                  </button>

                  <button
                    onClick={handleSend}
                    disabled={(!input.trim() && attachments.length === 0) || isLoading}
                    className="rounded-lg bg-[#00ff9d] px-4 py-1.5 text-[11px] font-bold text-black transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    SEND
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => scrollToBottom('smooth')}
        className={`fixed bottom-5 right-5 z-[80] flex h-11 w-11 items-center justify-center rounded-full border shadow-lg transition-all ${
          isNearBottom
            ? 'border-[#00ff9d]/20 bg-[#00ff9d]/12 text-[#00ff9d]/70'
            : 'border-[#00ff9d] bg-[#00ff9d] text-black'
        }`}
        title="Jump to bottom"
      >
        <span className="relative flex items-center justify-center">
          <svg className="h-4.5 w-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 10l7 7 7-7" />
          </svg>
          {!isNearBottom && newMessageCount > 0 && (
            <span className="absolute -right-2 -top-2 min-h-5 min-w-5 rounded-full bg-red-500 px-1 text-center text-[10px] leading-5 text-white">
              {newMessageCount}
            </span>
          )}
        </span>
      </button>

      {showJumpTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 right-5 z-[80] flex h-11 w-11 items-center justify-center rounded-full border border-[#00ff9d]/20 bg-[#111] text-[#00ff9d]/80 shadow-lg transition-all hover:bg-[#00ff9d]/10"
          title="Jump to top"
        >
          <svg className="h-4.5 w-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 14l-7-7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default CodingTerminal;
